import { stringToCentAmount } from '../../../utils';

import {
  NON_DETERMINISTIC_ATTRIBUTES,
  VARIANT_ATTRIBUTE_NAMES_COLOR,
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
} from './constants';

const REGEX_TEMPLATE = /__([^_]+)_(\d+)__/gi;
const REGEX_IS_ATTRIBUTE_CAPITALIZED = /([^_]+)-capitalize/i;

export const isColorAttribute = ({ attributeName }) =>
  VARIANT_ATTRIBUTE_NAMES_COLOR.includes(attributeName);

export const getVariantLowestPrice = (variants) =>
  variants.reduce(
    (prev, curr) => {
      const currPrice = stringToCentAmount({
        value: curr.price,
      });

      return currPrice.centAmount < prev.centAmount ? currPrice : prev;
    },
    stringToCentAmount({
      value: variants[0].price,
    }),
  );

export const getVariantsWithFixedColorCodeKey = (variants, attributeName) => {
  let variantsWithFixedColorCodeKey = null;

  if (attributeName === VARIANT_ATTRIBUTE_NAME_COLOR_CODE) {
    variantsWithFixedColorCodeKey = variants.map((currentVariant) => ({
      ...currentVariant,
      attributes: currentVariant.attributes.map((attribute) => {
        if (attribute.name === VARIANT_ATTRIBUTE_NAME_COLOR_CODE) {
          return {
            ...attribute,
            key: currentVariant.attributes.find((attrib) =>
              isColorAttribute({ attributeName: attrib.name }),
            )?.key,
          };
        }
        return attribute;
      }),
    }));
  } else {
    variantsWithFixedColorCodeKey = variants;
  }
  return variantsWithFixedColorCodeKey;
};

export const getOptions = (variants, locale) => {
  const colorAttribute = variants?.[0].attributes.find(({ name }) =>
    isColorAttribute({ attributeName: name }),
  );

  const sizeAttributes = variants?.[0].attributes.filter(
    ({ name }) => name.toLowerCase().indexOf('size') >= 0,
  );

  const deterministicAttributes = [
    colorAttribute,
    ...sizeAttributes,
    ...variants?.[0].attributes
      .filter(
        ({ name }) =>
          !isColorAttribute({ attributeName: name }) &&
          name.toLowerCase().indexOf('size') < 0 &&
          !NON_DETERMINISTIC_ATTRIBUTES.includes(name),
      )
      .sort((a, b) => (a.name > b.name ? 1 : -1)),
  ].filter(Boolean);

  const attributeNames =
    deterministicAttributes.map((attribute) => attribute.name) ?? [];

  const optionsArray = attributeNames?.map((attributeName) => {
    const variantsWithFixedColorCodeKey = getVariantsWithFixedColorCodeKey(
      variants,
      attributeName,
    );

    const attributeValues = variantsWithFixedColorCodeKey?.map((variant) => {
      const selectedAttribute = variant.attributes?.find(
        (attribute) => attribute.name === attributeName,
      );
      if (selectedAttribute) {
        return Object.fromEntries([
          ...Object.entries(selectedAttribute).filter(([key]) =>
            ['key', 'label'].includes(key),
          ),
        ]);
      }
      return [];
    });
    return {
      [attributeName]: {
        label: { [locale]: attributeName },
        value: [...new Set(attributeValues.map((v) => v.key))].map(
          (attributeValueKey) =>
            attributeValues.find((v) => v.key === attributeValueKey),
        ),
      },
    };
  });

  const options = optionsArray?.reduce(
    (acc, value) => ({ ...acc, ...value }),
    {},
  );

  const attributesToDisplay = optionsArray
    ?.map((option) => {
      if (option[Object.keys(option)[0]].value.length > 1) {
        return Object.keys(option)[0];
      }
      return undefined;
    })
    .filter(Boolean);

  return {
    attributeNames: attributeNames.filter((name) =>
      attributesToDisplay.find((display) => display === name),
    ),
    options,
  };
};

export const buildTextFromVariantAttributes = ({ template, variants }) => {
  const replaceAttributesByValue = (
    _,
    attributeNameToBeAnalyzed,
    variantIndex,
  ) => {
    if (variantIndex > variants.length) {
      return '';
    }

    const foundVariant = variants[variantIndex - 1];

    const shouldCapitalizeAttributeValue = attributeNameToBeAnalyzed.match(
      REGEX_IS_ATTRIBUTE_CAPITALIZED,
    );

    const attributeName =
      attributeNameToBeAnalyzed.match(REGEX_IS_ATTRIBUTE_CAPITALIZED)?.[1] ??
      attributeNameToBeAnalyzed;

    const foundVariantAttribute = foundVariant?.attributes?.find(
      (attribute) => attribute.name === attributeName,
    );

    if (!foundVariantAttribute) {
      return '';
    }
    const attributeValue = foundVariantAttribute.label?.toLowerCase() ?? '';
    return shouldCapitalizeAttributeValue
      ? attributeValue
        .split(' ')
        .map((word) => `${word?.charAt(0).toUpperCase()}${word?.slice(1)}`)
        ?.join(' ')
      : attributeValue;
  };
  return template.replace(REGEX_TEMPLATE, replaceAttributesByValue);
};
